import React, { useState, useEffect, useCallback, useRef } from "react";

// recoil
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userCredentials, routeInfo } from "recoilState/GlobalState";

//Services
import { zonaGet } from "services/services-mia/miakey";
import Modal from "components/UI/Modal/Modal";
import { useService } from "components/route/ServiceRoute";

// utils
import XGrid from "components/UI/Grid/XGrid";
import { HeaderButton, Buttons, Button } from "components";
import { Add, Cached, DeleteOutline, EditOutlined, FilterAltOffRounded } from "@mui/icons-material";

import { DeleteModal, InsertModal, UpdateModal } from "./Actions";

// consts
import { sucursales as titles } from "consts/titulos-de-tablas";
import { ACTIONS } from "consts/Actions";
import { Routes } from "consts/Routes-App";

// styles
import s from "styles/pages/Pages.module.scss"
import Filters from "components/Filters";
import { useFormik } from "formik";

export default function Zonas() {
    const [modalDetails, setModalDetails] = useState({ isOpen: false });
    const [isLoading, setIsLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const allRowsRef = useRef([]);
    const [rowSelected, setRowSelected] = useState({ status: false, row: {} })

    const { service } = useService();

    const credentials = useRecoilValue(userCredentials);
    const setRoute = useSetRecoilState(routeInfo);

    const formik = useFormik({
        initialValues: {
            zonas: "",
        },
        onSubmit: (values) => {
            const filtered = allRowsRef.current.filter((row) =>
                row.gateZonaNombre?.toLowerCase().includes(values.zonas.toLowerCase())
            );
            setRows(filtered);
        },
    });

    const resetFilters = () => {
        formik.resetForm();
        setRows([...allRowsRef.current]);
    };

    const updateList = useCallback(async () => {
        setRowSelected({ status: false, row: {} });
        setIsLoading(true);
        setRows([]);
        const { result } = await zonaGet({}, credentials);
        const mappedRows = result.map((item) => {
            const {
                gateZonaEsDefault, gateZonaId, gateZonaNombre,
                gateZonaUFH, gateZonaUID, gateZonaVersion,
                merchantId, merchantNombre, merchantSucursalId,
                merchantSucursalNombre, gateZonaCod, gateZonaGlobal,
            } = item;

            return {
                id: gateZonaId,
                gateZonaEsDefault, gateZonaId, gateZonaNombre,
                gateZonaUFH, gateZonaUID, gateZonaVersion,
                merchantId, merchantNombre, merchantSucursalId,
                merchantSucursalNombre, gateZonaCod, gateZonaGlobal,
                [titles[0].field]: gateZonaCod,
                [titles[1].field]: gateZonaGlobal === "Y" ? "Si" : "No",
                [titles[2].field]: gateZonaNombre,
            };
        });
        allRowsRef.current = mappedRows;
        setRows(mappedRows);
        setIsLoading(false);
    }, [credentials]);

    useEffect(() => {
        setRoute(Routes.zonas)
    }, [setRoute])

    useEffect(() => {
        updateList()
    }, [service, updateList])

    const handleClick = ({ procede, title }) => {
        setModalDetails({ title, procede, isOpen: true });
    };

    const closeModal = () => {
        setModalDetails({ isOpen: false });
    };

    return (
        <main className={s.main}>
            <Modal onClose={closeModal} open={modalDetails.isOpen} title={modalDetails.title}>
                {modalDetails.procede === ACTIONS.UPDATE && (
                    <UpdateModal
                        zona={rowSelected.row}
                        updateList={updateList}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.DELETE && (
                    <DeleteModal
                        zona={rowSelected.row}
                        updateList={updateList}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.INSERT && (
                    <InsertModal
                        updateList={updateList}
                        closeModal={closeModal}
                    />
                )}
            </Modal>
            <div className={s["filters-actions"]}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Filters
                        formik={formik}
                        inputsRender={[
                            { type: "text", name: "zonas", label: "Nombre de Zona", width: "225px", disabled: false },
                        ]}
                    />
                    <HeaderButton onClick={resetFilters}>
                        <FilterAltOffRounded />
                    </HeaderButton>
                </div>
                <Buttons>
                    <HeaderButton
                        text={"Actualizar"}
                        onClick={updateList}
                        disabled={rowSelected.row.id}
                    >
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        text={"Agregar Zona"}
                        handleClick={handleClick}
                        data={{
                            title: "Agregar Zona",
                            procede: ACTIONS.INSERT
                        }}
                        disabled={rowSelected.row.id || service.servicioPermiso !== 'E'}
                    >
                        <Add />
                    </HeaderButton>
                    <HeaderButton
                        text={"Editar Zona"}
                        handleClick={handleClick}
                        data={{
                            title: "Editar Zona",
                            procede: ACTIONS.UPDATE
                        }}
                        disabled={!rowSelected.row.id || service.servicioPermiso !== 'E'}
                    >
                        <EditOutlined />
                    </HeaderButton>

                    <HeaderButton
                        text={"Eliminar Zona"}
                        handleClick={handleClick}
                        data={{
                            title: "Eliminar Zona",
                            procede: ACTIONS.DELETE,
                        }}
                        disabled={!rowSelected.row.id || service.servicioPermiso !== 'E'}
                    >
                        <DeleteOutline />
                    </HeaderButton>
                </Buttons>
            </div>
            <div className={s.container}>
                <XGrid
                    rows={rows}
                    titles={titles}
                    loading={isLoading}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                />
            </div>
        </main>
    );
}
